import React, { useEffect, useState } from "react"
import {
    EPayFacType,
    EProviderName,
    ETermsType,
    IKycFlowDto,
    IProviderConfig,
    MixedMerchantDetails,
    TFeatureType,
    TOnboardingStatus,
    TPayment,
    getCompanyInfo,
    getConfiguredProviderForPaymentType,
    getDefaultProviderForPaymentType,
    getTermsForPayFacs,
    getText,
    isKycFlowsOnboardedForPayFac,
    organizationSelectors,
    useSelector,
} from "swiipe.portal.shared"
import CvrNotFoundDialog from "../components/servicesonboarding/CvrNotFoundDialog"
import { StoreState } from "../store/StoreState"
import { IWebshopProviderConfig } from "../store/reducers/merchantServicesReducer"
import { useReduxDispatch } from "../store/useReduxDispatch"
import { navigationService } from "./navigationService"
import { getProviderStatus } from "./paymentService"

export type TOnboardingType =
    | "mpo"
    | "cards"
    | "dankort"
    | "forbrugsforeningen"
    | "winback"
    | "plusSell"
    | "anywhereMobile"
    | "returnSell"
    | "viabill"
    | "anyday"
    | "swish"
    | "klarna"
    | "vipps"
    | "applepay"
    | "accountToAccount"
    | "resurs"
    | "externalAnalytics"
    | "bancontact"
    | "ideal"
    | "termstech"

export function isWebshopOnboardedForFlow(
    webshopId: string,
    companyCountryCode: string,
    config: IWebshopProviderConfig | undefined,
    onboardingType: TOnboardingType,
    kycFlows: IKycFlowDto[] | undefined,
    providerConfigs: IProviderConfig[],
    requireReady?: boolean
) {
    if (!config || !config.gatewayProviders || !kycFlows) {
        return false
    }

    const acceptableStatuses: (TOnboardingStatus | undefined)[] = requireReady ? ["Ready"] : ["InProgress", "Ready"]

    switch (onboardingType) {
        case "cards": {
            const provider = getDefaultProviderForPaymentType(companyCountryCode, "CreditCard")
            if (provider === "PaymentIQ") {
                const creditCardStatus = getProviderStatus(config.gatewayProviders, "CreditCard", "Clearhaus")
                if (acceptableStatuses.includes(creditCardStatus)) {
                    return true
                }
            }
            if (provider === "Digiteal") {
                const isOnboardedKyc = isKycFlowsOnboardedForPayFac(undefined, [webshopId], kycFlows, "Digiteal")
                const creditCardStatus = getProviderStatus(config.gatewayProviders, "CreditCard", "Digiteal")
                if (isOnboardedKyc && acceptableStatuses.includes(creditCardStatus)) {
                    return true
                }
            }
            break
        }
        case "mpo": {
            const clearhausMpoStatus = getProviderStatus(config.gatewayProviders, "MobilePayOnline", "Clearhaus")
            const mpoStatus = getProviderStatus(config.gatewayProviders, "MobilePayOnline", "MobilePayOnline")
            if (acceptableStatuses.includes(clearhausMpoStatus) && acceptableStatuses.includes(mpoStatus)) {
                return true
            }
            break
        }
        case "dankort": {
            const dankortStatus = getProviderStatus(config.gatewayProviders, "CreditCard", "DankortEpay")
            if (acceptableStatuses.includes(dankortStatus)) {
                return true
            }
            break
        }
        case "swish": {
            const swishStatus = getProviderStatus(config.gatewayProviders, "Swish", "Swish")
            if (acceptableStatuses.includes(swishStatus)) {
                return true
            }
            break
        }
        case "forbrugsforeningen": {
            const forbrugsforeningenStatus = getProviderStatus(config.gatewayProviders, "CreditCard", "ForbrugsforeningenEpay")
            if (acceptableStatuses.includes(forbrugsforeningenStatus)) {
                return true
            }
            break
        }
        case "viabill": {
            const viabillStatus = getProviderStatus(config.gatewayProviders, "Viabill", "Viabill")
            if (acceptableStatuses.includes(viabillStatus)) {
                return true
            }
            break
        }
        case "anyday": {
            const anydayStatus = getProviderStatus(config.gatewayProviders, "Anyday", "Anyday")
            if (acceptableStatuses.includes(anydayStatus)) {
                return true
            }
            break
        }
        case "bancontact": {
            const isOnboardedKyc = isKycFlowsOnboardedForPayFac(undefined, [webshopId], kycFlows, "Digiteal")
            const bancontactStatus = getProviderStatus(config.gatewayProviders, "Bancontact", "Digiteal")
            if (isOnboardedKyc && acceptableStatuses.includes(bancontactStatus)) {
                return true
            }
            break
        }
        case "ideal": {
            const isOnboardedKyc = isKycFlowsOnboardedForPayFac(undefined, [webshopId], kycFlows, "Digiteal")
            const idealStatus = getProviderStatus(config.gatewayProviders, "Ideal", "Digiteal")
            if (isOnboardedKyc && acceptableStatuses.includes(idealStatus)) {
                return true
            }
            break
        }
        case "klarna": {
            const klarnaPayLaterStatus = getProviderStatus(config.gatewayProviders, "KlarnaPayLater", "Klarna")
            const klarnaFinancingStatus = getProviderStatus(config.gatewayProviders, "KlarnaFinancing", "Klarna")
            if (acceptableStatuses.includes(klarnaPayLaterStatus) && acceptableStatuses.includes(klarnaFinancingStatus)) {
                return true
            }
            break
        }
        case "vipps": {
            const vippsProvider = getConfiguredProviderForPaymentType(providerConfigs, webshopId, "Vipps", companyCountryCode)
            const vmpVippsStatus = getProviderStatus(config.gatewayProviders, "Vipps", vippsProvider)
            if (acceptableStatuses.includes(vmpVippsStatus)) {
                return true
            }
            break
        }
        case "applepay": {
            const clearhausApplePayStatus = getProviderStatus(config.gatewayProviders, "ApplePay", "Clearhaus")
            const applePayStatus = getProviderStatus(config.gatewayProviders, "ApplePay", "ApplePay")
            if (acceptableStatuses.includes(clearhausApplePayStatus) && acceptableStatuses.includes(applePayStatus)) {
                return true
            }
            break
        }
        case "resurs": {
            const resusrInvoiceStatus = getProviderStatus(config.gatewayProviders, "ResursInvoice", "Resurs")
            const resusrPartPaymentStatus = getProviderStatus(config.gatewayProviders, "ResursPartPayment", "Resurs")
            const resusRevolvingCreditStatus = getProviderStatus(config.gatewayProviders, "ResursRevolvingCredit", "Resurs")
            if (
                acceptableStatuses.includes(resusrInvoiceStatus) ||
                acceptableStatuses.includes(resusrPartPaymentStatus) ||
                acceptableStatuses.includes(resusRevolvingCreditStatus)
            ) {
                return true
            }
            break
        }
        case "termstech": {
            const termsTechStatus = getProviderStatus(config.gatewayProviders, "TermsTech", "Area42")
            if (acceptableStatuses.includes(termsTechStatus)) {
                return true
            }
            break
        }
    }
    return false
}

export const getServiceNameFromOnboardingType = (onboardingType: TOnboardingType) => {
    switch (onboardingType) {
        case "mpo":
            return getText("serviceNames.mpo")
        case "dankort":
            return getText("serviceNames.dankort")
        case "forbrugsforeningen":
            return getText("serviceNames.forbrugsforeningen")
        case "winback":
            return getText("serviceNames.winback")
        case "plusSell":
            return getText("serviceNames.plusSell")
        case "anywhereMobile":
            return getText("serviceNames.anywhereMobile")
        case "returnSell":
            return getText("serviceNames.returnSell")
        case "viabill":
            return getText("serviceNames.viabill")
        case "anyday":
            return getText("serviceNames.anyday")
        case "bancontact":
            return getText("serviceNames.bancontact")
        case "ideal":
            return getText("serviceNames.ideal")
        case "swish":
            return getText("serviceNames.swish")
        case "klarna":
            return getText("serviceNames.klarna")
        case "vipps":
            return getText("serviceNames.vipps")
        case "applepay":
            return getText("serviceNames.applepay")
        case "accountToAccount":
            return getText("serviceNames.a2a")
        case "resurs":
            return "Resurs"
        case "cards":
            return "Clearhaus"
        case "externalAnalytics":
            return "Analytics"
        case "termstech":
            return getText("serviceNames.termsTech")
    }
}

export const getFeatureTypeFromOnboardingType = (onboardingType: TOnboardingType): TFeatureType | undefined => {
    switch (onboardingType) {
        case "winback":
            return "Winback"
        case "plusSell":
            return "PlusSell"
        case "returnSell":
            return "ReturnSell"
        case "anywhereMobile":
            return "AnywhereMobile"
        case "externalAnalytics":
            return "ExternalAnalytics"
        default:
            return undefined
    }
}

export function onboardingTypeIsAnyOf(onboardingType: TOnboardingType, onboardingTypes: TOnboardingType[]) {
    return onboardingTypes.some((type) => type === onboardingType)
}

export interface IOnboardingComponentsConfigEntity {
    component: JSX.Element | null
    condition: boolean
    id: TOnboardingStepId
}
export type TOnFirstStepGoBack = "showWebshopSelectAgain" | "navigateToPluginsPage"
export type TOnboardingStepId =
    | "sharedNoteDialog"
    | "acquiringPricingDialog"
    | "pspOnboarding"
    | "clearhausWithoutDataOnboarding"
    | "clearhausOnboarding"
    | "onboardingPricingDialog"
    | "dankortOnboarding"
    | "forbrugsforeningenOnboarding"
    | "hasViabillAlready"
    | "viabillOnboarding"
    | "anydayOnboarding"
    | "bancontactOnboarding"
    | "idealOnboarding"
    | "swishOnboarding"
    | "vippsOnboarding"
    | "applePayOnboarding"
    | "klarnaOnboarding"
    | "mpoOnboarding"
    | "a2aOnboarding"
    | "resursOnboarding"
    | "anywhereMobileOnboarding"
    | "plusSellDisclaimer"
    | "plusSellOnboarding"
    | "returnSellOnboarding"
    | "winbackOnboarding"
    | "submittedDialog"
    | "externalAnalyticsOnboarding"
    | "digitealKycOnboarding"
    | "termsTechOnboarding"
export function useServicesOnboardingPage(
    configRef: React.MutableRefObject<IOnboardingComponentsConfigEntity[]>,
    merchantDetails: MixedMerchantDetails,
    onFirstStepGoBack: TOnFirstStepGoBack
) {
    const dispatch = useReduxDispatch()

    const isCvrNotFound = useSelector<StoreState, boolean | undefined>((state) =>
        organizationSelectors.isCvrNotFound(state, merchantDetails.swMerchant.registrationNumber)
    )

    const [shouldSkipStepIds, setShouldSkipStepIds] = useState<TOnboardingStepId[]>([])
    const [shouldPreventSkipStepIds, setShouldPreventSkipStepIds] = useState<TOnboardingStepId[]>([])

    const [activeStepId, setActiveStepId] = useState("")
    const [isWebshopsSelected, setIsWebshopsSelected] = useState(false)

    useEffect(() => {
        if (isWebshopsSelected) {
            incrementStep("nextStep")
        }
    }, [isWebshopsSelected])

    useEffect(() => {
        setIsWebshopsSelected(false)
        setActiveStepId("")

        dispatch(getCompanyInfo(merchantDetails.swMerchant.countryCode, merchantDetails.swMerchant.registrationNumber))
    }, [merchantDetails])

    useEffect(() => {
        if (!isCvrNotFound) {
            return
        }
        // if cvr not found return <CvrNotFoundDialog> as active component
        // and set isWebshopsSelected so <WebshopSelectDialog> is hidden
        setIsWebshopsSelected(true)
    }, [isCvrNotFound])

    const incrementStep = (increment: "nextStep" | "previousStep") => {
        const stepsConfig = increment === "nextStep" ? configRef.current : configRef.current.reverse()

        if (increment === "previousStep" && activeStepId === "") {
            setIsWebshopsSelected(false)
            return
        }

        let previouslySelectedStepReached = false
        let stepSelected = false

        for (let i = 0; i < stepsConfig.length; i++) {
            const stepConfig = stepsConfig[i]
            const stepId = stepConfig.id

            const shouldSkipStep = shouldSkipStepIds.includes(stepId)
            const preventSkipStep = shouldPreventSkipStepIds.includes(stepId)

            if (activeStepId == stepId) {
                previouslySelectedStepReached = true
                continue
            }

            if (
                (previouslySelectedStepReached || activeStepId === "") &&
                !shouldSkipStep &&
                (stepConfig.condition || preventSkipStep)
            ) {
                setActiveStepId(stepId)
                stepSelected = true
                break
            }
        }

        if (!stepSelected) {
            setActiveStepId("")

            if (increment === "nextStep") {
                return
            }

            if (onFirstStepGoBack === "navigateToPluginsPage") {
                navigationService.navigate("/plugins")
                return
            }

            if (onFirstStepGoBack === "showWebshopSelectAgain") {
                setIsWebshopsSelected(false)
                setActiveStepId("")
            }
        }
    }

    return {
        isWebshopsSelected,
        setIsWebshopsSelected: (isWebshopsSelected: boolean) => setIsWebshopsSelected(isWebshopsSelected),
        getStepToShow: () => {
            if (isCvrNotFound) {
                return <CvrNotFoundDialog merchantDetails={merchantDetails} />
            }

            if (!isWebshopsSelected) {
                return null
            }

            return configRef.current.find((c) => c.id === activeStepId)?.component || null
        },
        incrementStep,
        addStepShouldSkip: (stepId: TOnboardingStepId) => {
            if (shouldSkipStepIds.includes(stepId)) {
                return
            }
            setShouldSkipStepIds((shouldSkipStepIds) => [...shouldSkipStepIds, stepId])
        },
        addStepPreventSkip: (stepId: TOnboardingStepId) => {
            if (shouldPreventSkipStepIds.includes(stepId)) {
                return
            }
            setShouldPreventSkipStepIds((shouldPreventSkipStepIds) => [...shouldPreventSkipStepIds, stepId])
        },
    }
}

const onboardingTypeToTermsType: { [onboardingType in TOnboardingType]: ETermsType } = {
    viabill: "TermsViaBill",
    anyday: "TermsAnyday",
    dankort: "TermsDankort",
    forbrugsforeningen: "TermsForbrugsforeningen",
    cards: "TermsAcquiringClearhaus", // fallback
    plusSell: "TermsPlusSell",
    returnSell: "TermsReturnSell",
    anywhereMobile: "TermsAnywhereMobile",
    winback: "TermsWinBack",
    mpo: "TermsMobilePayOnline",
    swish: "TermsSwish",
    klarna: "TermsKlarna",
    vipps: "TermsVippsEpayment",
    applepay: "TermsApplePay",
    accountToAccount: "TermsA2a",
    resurs: "TermsResurs",
    externalAnalytics: "TermsExternalAnalytics",
    bancontact: "TermsBancontact",
    ideal: "TermsIdeal",
    termstech: "TermsTermsTech",
}

export function termsTypeForOnboardingType(
    onboardingType: TOnboardingType,
    configuredProvider: EProviderName | undefined,
    payFacType: EPayFacType | undefined
) {
    if (onboardingType === "cards" && payFacType) {
        const payFacTerms = getTermsForPayFacs([payFacType])
        if (payFacTerms.length > 0) {
            return payFacTerms[0]
        }
    }
    if (onboardingType === "vipps") {
        return configuredProvider === "VippsMobilePay" ? "TermsVippsEpayment" : "TermsVippsPsp"
    }
    return onboardingTypeToTermsType[onboardingType]
}

const onboardingTypeToPaymentType: { [onboardingType in TOnboardingType]: TPayment[] } = {
    viabill: ["Viabill"],
    anyday: ["Anyday"],
    dankort: ["CreditCard"],
    forbrugsforeningen: ["CreditCard"],
    cards: ["CreditCard"],
    plusSell: [],
    returnSell: [],
    anywhereMobile: [],
    winback: [],
    mpo: ["MobilePayOnline"],
    swish: ["Swish"],
    klarna: ["KlarnaFinancing", "KlarnaPayLater"],
    vipps: ["Vipps"],
    applepay: ["ApplePay"],
    accountToAccount: ["AccountToAccount"],
    resurs: ["ResursInvoice", "ResursPartPayment", "ResursRevolvingCredit"],
    externalAnalytics: [],
    bancontact: ["Bancontact"],
    ideal: ["Ideal"],
    termstech: ["TermsTech"],
}

export function getPaymentTypesForOnboardingType(onboardingType: TOnboardingType): TPayment[] {
    return onboardingTypeToPaymentType[onboardingType]
}

const onboardingTypeToSupportedPayFacTypes: { [onboardingType in TOnboardingType]: EPayFacType[] } = {
    viabill: [],
    anyday: [],
    dankort: ["Clearhaus"], // Temporary hack - we couple PamentIQ Dankort flow to credit card with Clearhaus onboarding
    forbrugsforeningen: ["Clearhaus"], // Temporary hack - we couple PamentIQ Dankort flow to credit card with Clearhaus onboarding
    cards: ["Clearhaus", "Shift4", "Digiteal"],
    plusSell: [],
    returnSell: [],
    anywhereMobile: [],
    winback: [],
    mpo: ["Clearhaus", "Shift4"],
    swish: [],
    klarna: [],
    vipps: ["Clearhaus"],
    applepay: ["Clearhaus"],
    accountToAccount: [],
    resurs: [],
    externalAnalytics: [],
    bancontact: ["Digiteal"],
    ideal: ["Digiteal"],
    termstech: ["Digiteal"],
}

export function getPayFacTypesForOnboardingType(onboardingType: TOnboardingType): EPayFacType[] {
    return onboardingTypeToSupportedPayFacTypes[onboardingType]
}
